import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DeckCard } from './DeckCard';
import { ConfirmDialog } from './ConfirmDialog';
import { UserProfile } from './UserProfile';

interface Deck {
  id: number;
  public_id: string;
  title: string;
  description?: string;
  slug?: string;
  public?: boolean;
  created_at: string;
  updated_at: string;
}

interface User {
  id: number;
  name: string;
  email: string;
  slug?: string;
}

interface DashboardProps {
  user: User;
  onLogout: () => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ user: initialUser, onLogout }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User>(initialUser);
  const [decks, setDecks] = useState<Deck[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; deck: Deck | null }>({
    open: false,
    deck: null
  });

  useEffect(() => {
    fetchDecks();
  }, []);

  const fetchDecks = async () => {
    try {
      const response = await fetch('/api/v1/decks', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response && response.ok) {
        const data = await response.json();
        setDecks(data.decks);
      }
    } catch (err) {
      console.error('Failed to fetch decks:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDeck = async () => {
    const title = `New Deck ${decks.length + 1}`;
    
    try {
      const response = await fetch('/api/v1/decks', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ deck: { title } }),
      });

      if (response.ok) {
        const data = await response.json();
        setDecks([data.deck, ...decks]);
      }
    } catch (err) {
      console.error('Failed to create deck:', err);
    }
  };

  const handleDuplicateDeck = async (deck: Deck) => {
    try {
      const response = await fetch(`/api/v1/decks/${deck.id}/duplicate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setDecks([data.deck, ...decks]);
      } else {
        console.error('Failed to duplicate deck');
      }
    } catch (err) {
      console.error('Failed to duplicate deck:', err);
    }
  };

  const handleDeleteDeck = async () => {
    if (!deleteDialog.deck) return;

    try {
      const response = await fetch(`/api/v1/decks/${deleteDialog.deck.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        setDecks(decks.filter(d => d.id !== deleteDialog.deck!.id));
      } else {
        console.error('Failed to delete deck');
      }
    } catch (err) {
      console.error('Failed to delete deck:', err);
    }
  };

  const openDeleteDialog = (deck: Deck) => {
    setDeleteDialog({ open: true, deck });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-lg text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex">
      {/* Sidebar */}
      <div className="w-64 bg-gray-800 border-r border-gray-700 flex flex-col">
        <div className="p-6">
          <h1 className="text-2xl font-bold text-white">Crudo</h1>
        </div>
        <UserProfile user={user} onUpdate={setUser} />
        <nav className="px-6 mt-auto pb-6">
          <button
            onClick={onLogout}
            className="text-gray-400 hover:text-white text-sm"
          >
            Sign Out
          </button>
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-8">
        <div className="max-w-4xl">
          <h2 className="text-2xl font-semibold text-white mb-8">My Decks</h2>
          
          <div className="space-y-4">
            {decks.map(deck => (
              <DeckCard
                key={deck.id}
                deck={deck}
                onClick={() => navigate(`/${user.slug || user.id}/${deck.slug || deck.public_id}`)}
                onDuplicate={() => handleDuplicateDeck(deck)}
                onDelete={() => openDeleteDialog(deck)}
              />
            ))}
            
            <button
              onClick={handleCreateDeck}
              className="w-full p-6 bg-gray-800 rounded-lg border border-gray-700 hover:border-gray-600 transition-colors flex items-center justify-center group"
            >
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gray-700 rounded flex items-center justify-center group-hover:bg-gray-600 transition-colors">
                  <span className="text-2xl text-gray-400 group-hover:text-gray-300">+</span>
                </div>
                <span className="text-lg text-gray-300">New Deck</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open, deck: deleteDialog.deck })}
        title="Delete Deck"
        description={`Are you sure you want to delete "${deleteDialog.deck?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteDeck}
        variant="danger"
      />
    </div>
  );
};